import { ScrapingField } from "@/types/scraper";

export interface AIDetectionResult {
  selector: string;
  confidence: number;
  value?: string;
  aiSuggested: boolean;
}

export interface RemoteAIConfig {
  provider: "deepseek" | "claude" | "local";
  apiKey?: string;
  endpoint?: string;
  modelName?: string;
  confidenceThreshold: number;
  enabled: boolean;
  requestTimeout?: number;
  maxRetries?: number;
  detectionMode?: "aggressive" | "balanced" | "conservative";
  includePageContext?: boolean;
  extractValues?: boolean;
}

export interface AIDetectionRequest {
  html: string;
  fieldId: string;
  fieldLabel?: string;
  fieldDescription?: string;
  url?: string;
}

/**
 * Handles communication with remote AI services for element detection
 */
export class RemoteAIService {
  private config: RemoteAIConfig;

  constructor(config: RemoteAIConfig) {
    this.config = config;
  }

  /**
   * Detect elements using the configured AI service
   */
  async detectElements(
    field: ScrapingField,
    html: string,
    url?: string,
  ): Promise<AIDetectionResult[]> {
    if (!this.config.enabled) {
      return [];
    }

    try {
      // Apply retry logic if configured
      const maxRetries = this.config.maxRetries || 2;
      const timeout = this.config.requestTimeout || 10000;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          // Set timeout for fetch operations
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);

          let results;
          switch (this.config.provider) {
            case "deepseek":
              results = await this.detectWithDeepSeek(
                field,
                html,
                url,
                controller.signal,
              );
              break;
            case "claude":
              results = await this.detectWithClaude(
                field,
                html,
                url,
                controller.signal,
              );
              break;
            case "local":
              results = await this.detectWithLocalService(
                field,
                html,
                url,
                controller.signal,
              );
              break;
            default:
              throw new Error(
                `Unsupported AI provider: ${this.config.provider}`,
              );
          }

          clearTimeout(timeoutId);
          return results;
        } catch (error) {
          if (attempt === maxRetries) {
            throw error; // Rethrow if we've exhausted retries
          }

          console.warn(
            `AI detection attempt ${attempt + 1}/${maxRetries + 1} failed, retrying...`,
          );
          // Wait with exponential backoff before retrying
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * Math.pow(2, attempt)),
          );
        }
      }

      // This should never be reached due to the throw in the catch block
      return [];
    } catch (error) {
      console.error(`AI detection error with ${this.config.provider}:`, error);
      return [];
    }
  }

  /**
   * Update the service configuration
   */
  updateConfig(config: Partial<RemoteAIConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get the current configuration
   */
  getConfig(): RemoteAIConfig {
    return { ...this.config };
  }

  /**
   * Detect elements using DeepSeek API
   */
  private async detectWithDeepSeek(
    field: ScrapingField,
    html: string,
    url?: string,
    signal?: AbortSignal,
  ): Promise<AIDetectionResult[]> {
    const requestData: AIDetectionRequest = {
      html,
      fieldId: field.id,
      fieldLabel: field.label,
      fieldDescription: field.description,
      url,
    };

    // Use custom endpoint if provided, otherwise use default
    const endpoint = this.config.endpoint || "/api/deepseek/detect";

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(this.config.apiKey
            ? { Authorization: `Bearer ${this.config.apiKey}` }
            : {}),
        },
        signal,
        body: JSON.stringify({
          ...requestData,
          modelName: this.config.modelName || "deepseek-coder-v1.5",
          confidenceThreshold: this.config.confidenceThreshold,
          detectionMode: this.config.detectionMode || "balanced",
          includePageContext: this.config.includePageContext || true,
          extractValues: this.config.extractValues || true,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `DeepSeek API error: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Unknown error from DeepSeek API");
      }

      return (data.results || []).map((result: any) => ({
        selector: result.selector,
        confidence: result.confidence,
        value: result.value,
        aiSuggested: true,
      }));
    } catch (error) {
      console.error("DeepSeek API error:", error);
      throw error;
    }
  }

  /**
   * Detect elements using Claude API
   */
  private async detectWithClaude(
    field: ScrapingField,
    html: string,
    url?: string,
    signal?: AbortSignal,
  ): Promise<AIDetectionResult[]> {
    const requestData: AIDetectionRequest = {
      html,
      fieldId: field.id,
      fieldLabel: field.label,
      fieldDescription: field.description,
      url,
    };

    // Use custom endpoint if provided, otherwise use default
    const endpoint = this.config.endpoint || "/api/claude/detect";

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(this.config.apiKey
            ? { Authorization: `Bearer ${this.config.apiKey}` }
            : {}),
        },
        signal,
        body: JSON.stringify({
          ...requestData,
          modelName: this.config.modelName || "claude-3-opus-20240229",
          confidenceThreshold: this.config.confidenceThreshold,
          detectionMode: this.config.detectionMode || "balanced",
          includePageContext: this.config.includePageContext || true,
          extractValues: this.config.extractValues || true,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Claude API error: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Unknown error from Claude API");
      }

      return (data.results || []).map((result: any) => ({
        selector: result.selector,
        confidence: result.confidence,
        value: result.value,
        aiSuggested: true,
      }));
    } catch (error) {
      console.error("Claude API error:", error);
      throw error;
    }
  }

  /**
   * Detect elements using local service (fallback)
   */
  private async detectWithLocalService(
    field: ScrapingField,
    html: string,
    url?: string,
    signal?: AbortSignal,
  ): Promise<AIDetectionResult[]> {
    // This is a fallback to the existing local implementation
    // It would call the local API endpoint that's already set up
    const endpoint = "/api/deepseek/detect";

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        signal,
        body: JSON.stringify({
          html,
          fieldId: field.id,
          modelName: "deepseek-coder-v1.5",
          confidenceThreshold: this.config.confidenceThreshold,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Local API error: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Unknown error from local API");
      }

      return (data.results || []).map((result: any) => ({
        selector: result.selector,
        confidence: result.confidence,
        value: result.value,
        aiSuggested: true,
      }));
    } catch (error) {
      console.error("Local API error:", error);
      throw error;
    }
  }
}

// Create a default instance with local provider
export const defaultRemoteAIService = new RemoteAIService({
  provider: "local",
  confidenceThreshold: 0.7,
  enabled: false,
  requestTimeout: 10000,
  maxRetries: 2,
  detectionMode: "balanced",
  includePageContext: true,
  extractValues: true,
});
